import React, { useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import ErrorMessage from '../../../components/error_message';
import RadioButtons from '../../../components/radio_buttons';
import EditFormField from '../../../components/edit_form_field';
import Input from 'react/components/input';
import Select from 'react/components/select';
import FeatureNames from 'constants/feature_names';

interface EditSequenceFormFieldUpdatePayloadProps {
  payload: any;
  stepIndex: number;
  updatePayload: (payload: any, stepIndex: number) => void;
  formFields: any[];
  displayValidationErrors: boolean;
}

const EditSequenceFormFieldUpdatePayload = ({
  payload,
  stepIndex,
  updatePayload,
  formFields,
  displayValidationErrors,
}: EditSequenceFormFieldUpdatePayloadProps) => {
  const [action, setAction] = useState(() => {
    if (payload.use_relative_custom_date) return 'relative_date';
    return [null, undefined].indexOf(payload.form_field_value) >= 0 ? 'blank' : 'value';
  });

  const field = useMemo(() => {
    if (!formFields?.length) {
      return null;
    }
    return formFields.find((item) => item.id === payload.form_field_id);
  }, [formFields, payload.form_field_id]);

  useEffect(() => {
    if (field?.required) {
      setAction('value');
    }
  }, [field]);

  const radioOptions = useMemo(() => {
    return field?.field_type === 'date' ? ['value', 'relative_date', 'blank'] : ['value', 'blank'];
  }, [field?.field_type]);

  const onUpdate = (updated) => {
    updatePayload({ ...payload, ...updated }, stepIndex);
  };

  const displayAvailableFor = (field) => {
    const types = _.compact([
      field.on_member && 'Members',
      field.on_prospect && 'Prospects',
      field.on_non_attending_contact && 'Non-attending Contacts',
    ]);
    if (types.length === 1) {
      return `${types[0]} only`;
    }

    if (types.length === 2) {
      return types.join(', ');
    }

    if (types.length === 3) {
      return `${types[0]}, ${types[1]} and ${types[2]}`;
    }
    return 'N/A';
  };

  if (!field && formFields) {
    return (
      <div className="template-details">
        <ErrorMessage
          error="The selected field has been removed. To resolve this issue, please remove this step and add
          a new step, if a new step is needed."
        />
      </div>
    );
  }

  const renderRadioLabel = (val) => {
    if (val === 'blank')
      return (
        <div className="margin-left">
          Blank {field.required && '(not available for required fields)'}
        </div>
      );

    if (val === 'relative_date')
      return (
        <div className="margin-left">
          <div>Relative date</div>
          <div className="inline-compact">
            <div className="inline-module">
              <Input
                size="xs"
                type="number"
                value={payload.relative_custom_date_number}
                onChange={(relative_custom_date_number) =>
                  onUpdate({ relative_custom_date_number })
                }
              />
            </div>
            <div className="inline-module">
              <Select
                disabled={!payload.use_relative_custom_date}
                size="sm"
                options={['days', 'weeks', 'months', 'years']}
                value={payload.relative_custom_date_period || 'days'}
                onChange={(relative_custom_date_period) =>
                  onUpdate({ relative_custom_date_period })
                }
              />
            </div>
            <div className="inline-module">
              <Select
                disabled={!payload.use_relative_custom_date}
                size="sm"
                options={['ago', 'from_now']}
                getLabel={(val) => (val === 'ago' ? 'before' : 'after')}
                value={payload.relative_custom_date_multiplier || 'from_now'}
                onChange={(relative_custom_date_multiplier) =>
                  onUpdate({ relative_custom_date_multiplier })
                }
              />
            </div>
            <div className="inline-module">this step fires</div>
          </div>
          {displayValidationErrors && !(payload.relative_custom_date_number >= 0) && (
            <ErrorMessage error="This field is invalid" />
          )}
        </div>
      );

    return (
      <div className="margin-left flex align-center">
        <div className="margin-right-8">New value</div>
        <div className="flex-grow">
          <EditFormField
            field={field}
            value={payload.form_field_value}
            onChange={(form_field_value) => onUpdate({ form_field_value })}
          />
          {displayValidationErrors &&
            field.required &&
            [null, undefined, ''].indexOf(payload.form_field_value) >= 0 && (
              <ErrorMessage error="This field is required" />
            )}
        </div>
      </div>
    );
  };

  if (!field) return null;

  return (
    <div className="template-details">
      <div className="row form-group">
        <div className="col-sm-4 col-lg-4">
          <label className="control-label">Field</label>
        </div>
        <div className="col-sm-8 col-lg-8">{field.label}</div>
      </div>

      <div className="row form-group">
        <div className="col-sm-4 col-lg-4">
          <label className="control-label">Available for</label>
        </div>
        <div className="col-sm-8 col-lg-8">
          {displayAvailableFor(field)}
          <div className="helper-text">
            Note: this change will not be applied if the form field is unavailable based on the
            contact's type.
          </div>
        </div>
      </div>

      <div className="row form-group">
        <div className="col-sm-4 col-lg-4">
          <label className="control-label">Set field to</label>
        </div>
        <div className="col-sm-8 col-lg-8">
          <RadioButtons
            options={radioOptions}
            value={action}
            onChange={(val) => {
              setAction(val);
              const update: any = {
                use_relative_custom_date: val === 'relative_date',
              };

              if (val === 'relative_date') {
                update.relative_custom_date_period = 'days';
                update.relative_custom_date_multiplier = 'from_now';
              } else {
                update.relative_custom_date_number = null;
                update.relative_custom_date_period = null;
                update.relative_custom_date_multiplier = null;
              }

              if (val === 'blank') {
                update.form_field_value = null;
              }

              onUpdate(update);
            }}
            element="div"
            disabledOptions={field.required ? ['blank'] : []}
            fullWidth
            vertical
            getValue={(val) => val}
            getLabel={renderRadioLabel}
          />
        </div>
      </div>

      <div>
        If the contact already has a value for this field, it will be overwritten when
        this {FeatureNames.SEQUENCE_LOWERCASE} runs.
      </div>
    </div>
  );
};

angular
  .module('gym')
  .value('EditSequenceFormFieldUpdatePayload', EditSequenceFormFieldUpdatePayload);
